/**
 * Step Study Dashboard: Active and Graduated cohorts.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../api/client';
import PageHeader from '../components/PageHeader';
import { usePermissions } from '../hooks/usePermissions';
import './StepStudyDashboard.css';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const StepStudyDashboard = () => {
  const { canCreateStepStudyCohort } = usePermissions();
  const [data, setData] = useState({ active: [], graduated: [], report: null });
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    get('/api/step-study/dashboard').then(({ ok, data: res }) => {
      if (ok && res) setData({
        active: res.active || [],
        graduated: res.graduated || [],
        report: res.report || null,
      });
      else setLoadError('Could not load Step Study data.');
      setIsLoading(false);
    });
  }, []);

  if (isLoading) return <div className="step-study-page"><p>Loading…</p></div>;
  if (loadError) return <div className="step-study-page"><p style={{ color: 'var(--cr-text)', fontSize: '0.9rem' }}>{loadError}</p></div>;

  return (
    <div className="step-study-page">
      <PageHeader title="Step Study Tracking" section="studies">
        <p className="step-study-subtitle">
          Track attendance and progress for Step Study classes
        </p>
      </PageHeader>

      <div className="step-study-dashboard-instructions-wrap">
        <button
          type="button"
          id="step-study-dashboard-instructions-toggle"
          aria-expanded={instructionsOpen}
          aria-controls="step-study-dashboard-instructions"
          className="step-study-dashboard-instructions-btn"
          onClick={() => setInstructionsOpen((o) => !o)}
        >
          <span aria-hidden>{instructionsOpen ? '▼' : '▶'}</span>
          <strong>How this page works</strong>
          <span className="step-study-dashboard-instructions-hint">
            {instructionsOpen ? '(hide)' : '(tap to expand)'}
          </span>
        </button>
        {instructionsOpen && (
          <div
            id="step-study-dashboard-instructions"
            className="step-study-instructions step-study-dashboard-instructions-body"
          >
            <p className="step-study-instructions-lead">
              New here? This screen is the <strong>dashboard</strong> for all Step Study classes. Use it to see what&apos;s running, open a class to enter weekly data, and view ministry-wide totals.
            </p>
            <ol>
              <li>
                <strong>What is a cohort?</strong> One <em>cohort</em> is one Step Study class—one group of people going through the lessons together, with its own leaders and dates.
              </li>
              <li>
                <strong>Two ways to track numbers (you choose per cohort):</strong> <strong>(1) Use a roster</strong> — turn on roster when you create or edit the cohort; add each person by name, record attendance, and weekly numbers can be tracked from that. <strong>(2) No roster</strong> — if you don&apos;t use a roster, you simply <strong>manually enter the numbers each week</strong> (active, dropped, new, lesson) in Add Weekly Entry—no name list.
              </li>
              <li>
                <strong>Active Step Studies</strong> — Classes that are still meeting. <strong>Tap a card</strong> to open that class. There you&apos;ll use either roster + attendance or manual weekly entries (see above), and when the class is done you can mark it graduated.
              </li>
              <li>
                <strong>+ New Cohort</strong> — Use this when a brand-new class starts and you need to track it here. (If you don&apos;t see this button, your account may only let you view existing classes—ask a ministry lead if you need to add classes.)
              </li>
              <li>
                <strong>Step Study Overall Report</strong> — Big-picture numbers for the whole ministry (for example, how many people are currently in Step Study and graduate totals). Helpful for reporting, not for editing one class.
              </li>
              <li>
                <strong>Graduated Step Studies</strong> — Classes that have finished. They stay listed so you can see history and final stats.
              </li>
            </ol>
          </div>
        )}
      </div>

      <section className="step-study-section">
        <h3>Active Step Studies</h3>
        {data.active.length === 0 ? (
          <p className="step-study-empty">
            No active cohorts{canCreateStepStudyCohort ? <>. <Link to="/step-study/new">Create one</Link>.</> : '.'}
          </p>
        ) : (
          <div className="step-study-cards">
            {data.active.map((c) => (
              <Link key={c._id} to={`/step-study/${c._id}`} className="step-study-card step-study-card-active">
                <div className="step-study-card-title">{c.displayName || c.classType}</div>
                <div className="step-study-card-meta">Started {formatDate(c.startDate)}</div>
                <div className="step-study-card-stats">
                  <span>Active: <strong>{c.currentActive}</strong></span>
                  <span>Lesson: <strong>{c.latestLesson ?? '—'}</strong></span>
                </div>
                {c.latestEntryDate && (
                  <div className="step-study-card-date">Last entry: {formatDate(c.latestEntryDate)}</div>
                )}
              </Link>
            ))}
          </div>
        )}
        {canCreateStepStudyCohort && (
        <div className="step-study-section-footer">
          <Link to="/step-study/new" className="step-study-btn step-study-btn-primary">+ New Cohort</Link>
        </div>
        )}
      </section>

      {data.report && (
        <section className="step-study-section" style={{ marginTop: '1.5rem' }}>
          <h3>Step Study Overall Report</h3>
          <div className="step-study-stats-box step-study-report-stats">
            <div className="step-study-stat">
              <span>Currently in Step Study</span>
              <strong>{data.report.currentlyInStepStudy ?? 0}</strong>
            </div>
            <div className="step-study-stat">
              <span>Graduated (total for Current YR)</span>
              <strong>{data.report.graduatedCurrentYear ?? 0}</strong>
            </div>
            <div className="step-study-stat">
              <span>Graduated (total since 2026)</span>
              <strong>{data.report.graduatedTotal ?? 0}</strong>
            </div>
            <div className="step-study-stat">
              <span>Flow-through (total for Current YR)</span>
              <strong>{data.report.flowThroughCurrentYear ?? 0}</strong>
            </div>
            <div className="step-study-stat">
              <span>Flow-through (total since 2026)</span>
              <strong>{data.report.flowThroughTotal ?? 0}</strong>
            </div>
          </div>
        </section>
      )}

      <section className="step-study-section">
        <h3>Graduated Step Studies</h3>
        {data.graduated.length === 0 ? (
          <p className="step-study-empty">No graduated cohorts yet.</p>
        ) : (
          <div className="step-study-cards">
            {data.graduated.map((c) => (
              <div key={c._id} className="step-study-card step-study-card-graduated">
                <div className="step-study-card-title">{c.displayName || c.classType}</div>
                <div className="step-study-card-meta">
                  {formatDate(c.startDate)} → {formatDate(c.graduationDate)}
                  {c.daysToGraduation != null && ` (${c.daysToGraduation} days)`}
                </div>
                <div className="step-study-card-stats step-study-card-stats-grid">
                  <span>Flow-through: <strong>{c.flowThrough}</strong></span>
                  <span>Graduated: <strong>{c.totalGraduated}</strong></span>
                  <span>Dropped: <strong>{c.totalDropped}</strong></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default StepStudyDashboard;
